import { Avatar } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Popover } from 'antd'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { RotaEventInstance } from 'screens/Rostering/types'
import styled from 'styled-components'
import EventIcon from '../EventIcon'

export type EventTitlePopoverOriginal = Pick<
  RotaEventInstance,
  'updatedBy' | 'updatedAt' | 'createdAt' | 'resident' | 'status' | 'title'
>

type EventTitlePopoverProps = {
  original?: EventTitlePopoverOriginal
  timeInterval?: string
  hoursText?: string
  children: ReactNode
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  disabled?: boolean
}

const EventTitlePopover = ({
  original,
  timeInterval,
  hoursText,
  children,
  isOpen,
  setIsOpen,
  disabled = false
}: EventTitlePopoverProps) => {
  const { t } = useTranslation()

  if (!original) {
    return null
  }

  const changedBy = original.updatedBy?.name
  const dateFormat = 'HH:mm DD/MM/YYYY'
  const changedAt =
    dayjs(original.updatedAt).format(dateFormat) !==
    dayjs(original.createdAt).format(dateFormat)
      ? dayjs(original.updatedAt).format(dateFormat)
      : undefined

  return (
    <Popover
      placement="topLeft"
      showArrow
      trigger={[]}
      open={disabled ? false : isOpen}
      onOpenChange={disabled ? undefined : setIsOpen}
      content={
        <ContentContainer>
          <UserAvatar>
            <StyledAvatar
              size={30}
              url={
                (original?.resident as unknown as { picture?: string })?.picture
              }
            />
            <PersonText weight="medium">
              {original?.resident?.name?.first} {original?.resident?.name?.last}
            </PersonText>
          </UserAvatar>
          <Divider />
          <PopoverHeader>
            <EventIcon status={original?.status} />
            <TitleText level={2}>{original?.status?.toUpperCase()}</TitleText>
          </PopoverHeader>
          <SubtitleText>{original?.title}</SubtitleText>
          <SubtitleText>
            {timeInterval} · {hoursText}
          </SubtitleText>
          {changedBy && changedAt && (
            <>
              <Divider />
              <ChangedByText level={2}>{t('LAST_CHANGED_BY')}</ChangedByText>
              <ChangedByText weight="regular">
                {changedBy} · {changedAt}
              </ChangedByText>
            </>
          )}
        </ContentContainer>
      }
    >
      {children}
    </Popover>
  )
}

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const TitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`
const SubtitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`
const ChangedByText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledAvatar = styled(Avatar)`
  border: 0.5px solid ${(props) => props.theme.color.neutral.color7};
`

const PersonText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default EventTitlePopover
